// IdleModal — surfaced by the idle-timer hook when the user has been idle past `timeoutInactivity`.
// Mirrors the Angular IdleModalComponent (stay / logout, with a countdown in the logout label).
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  remainingSeconds: number;
  onStay: () => void;
  onLogout: () => void;
}

export function IdleModal({ open, remainingSeconds, onStay, onLogout }: Props) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>{t('global.timeout_popup.title', { defaultValue: 'Session timeout' })}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="img"
            src="/assets/images/clock-icon.webp"
            alt="clock-icon"
            sx={{ width: 96, height: 96, objectFit: 'contain' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <Box>
            <Typography>{t('global.timeout_popup.text1', { defaultValue: 'You have been inactive.' })}</Typography>
            <Typography>{t('global.timeout_popup.text2', { defaultValue: 'You will be logged out soon.' })}</Typography>
            <Typography>{t('global.timeout_popup.text3', { defaultValue: 'Stay signed in?' })}</Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button color="error" variant="outlined" onClick={onLogout}>
          {t('global.timeout_popup.action1', { defaultValue: 'Logout' })} (
          <strong>{remainingSeconds}s</strong>)
        </Button>
        <Button color="success" variant="contained" onClick={onStay}>
          {t('global.timeout_popup.action2', { defaultValue: 'Stay signed in' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
